import styles from 'styles/modules/subscription/Card.module.scss';
import CheckMarkList from 'styles/modules/CheckMarkList.module.scss';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import CheckMark from 'components/Svg/CheckMark';
import Button from 'components/Button';
import Star from 'components/Svg/Star';
import { postReq } from 'api';
import { useRouter } from 'next/router';
import { subscriptions } from 'lang/en.json';
import SvgDiamond from '../Svg/diamond';
import { updateContributingEditorStatus } from '../../store/auth';

const Card = ({
    product: { name, marketing_features },
    data,
    plan: currentPlan,
    buttons,
    discount,
    monthly,
}) => {
    const [loading, setLoading] = useState(false);
    const [upgrade, setUpgrade] = useState(false);
    const dispatch = useDispatch();
    const router = useRouter();

    const tag =
        data?.type !== 'legend' &&
        currentPlan.type === data.type &&
        !currentPlan.registration ? (
            <div
                className={[styles.tag, styles[`${currentPlan?.type}Tag`]].join(
                    ' '
                )}
            >
                {currentPlan?.type === 'platinum' ? <SvgDiamond /> : <Star />}
                <div
                    className={[
                        styles.ribbon,
                        styles[`${currentPlan?.type}Ribbon`],
                    ].join(' ')}
                />
            </div>
        ) : null;

    const tagLegend = (
        <div className={[styles.tag, styles[`${data?.type}Tag`]].join(' ')}>
            <svg
                width="35"
                height="45"
                viewBox="0 0 35 45"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M0 29.6471V0H35V29.6471L17.2464 45L0 29.6471Z"
                    fill="white"
                />
                <path
                    d="M23.4621 20.9375L23.1735 22.1891C23.1145 22.4444 22.8872 22.625 22.6251 22.625H11.3751C11.113 22.625 10.8857 22.4444 10.8266 22.1891L10.5381 20.9375H23.4621Z"
                    fill="var(--color-blue)"
                />
                <path
                    d="M24.8612 14.8766L23.7222 19.8125H10.2784L9.13936 14.8766C9.08929 14.6589 9.17254 14.4317 9.35198 14.2983C9.53198 14.165 9.77329 14.151 9.96679 14.2618L13.4481 16.2513L16.5317 11.6259C16.6324 11.4752 16.7989 11.3818 16.9795 11.375C17.1617 11.3672 17.3333 11.4493 17.4441 11.5922L21.0604 16.2418L24.0006 14.282C24.1924 14.1549 24.4427 14.156 24.6323 14.2871C24.8224 14.4182 24.9124 14.6522 24.8612 14.8766V14.8766Z"
                    fill="var(--color-blue)"
                />
            </svg>
        </div>
    );

    const nextBilling = (
        <div className={styles.info}>
            {currentPlan.type === 'free' &&
                data.interval === 'year' &&
                data.type !== 'free' && (
                    <span>
                        Monthly price: £{(data.amount / 12 / 100).toFixed(2)}
                    </span>
                )}
        </div>
    );

    const handleManage = async () => {
        setLoading(true);
        try {
            const redirectUrl = (
                await postReq('/stripe/customer-portal/session')
            ).data;
            await router.push(redirectUrl);
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    const handleUpgrade = async () => {
        setLoading(true);
        try {
            const redirectUrl = (
                await postReq(
                    `/stripe/checkout/session/subscription/${
                        data.type
                    }?billing=${monthly ? 'monthly' : 'annual'}`
                )
            ).data;

            // If this is a Contributing Editor upgrade, update the status immediately
            if (data.type === 'contributing_editor') {
                dispatch(
                    updateContributingEditorStatus({
                        company: currentPlan.company,
                    })
                );
            }

            await router.push(redirectUrl);
        } catch (e) {
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    const button = {
        contributing_editor: {
            theme: 'primary',
            title: 'Upgrade',
            loading,
            registration: false,
            classes: [styles[data.type]],
            onClick: () => {
                if (buttons?.contributing_editor?.registration) {
                    // During registration, just call the registration handler
                    buttons.contributing_editor.onClick();
                } else {
                    // During normal upgrade, proceed with Stripe
                    handleUpgrade();
                }
            },
            ...(buttons && buttons.contributing_editor),
        },
        free: {
            theme: 'secondary',
            title: 'Manage my subscription',
            loading,
            classes: [styles[data.type]],
            onClick: () => handleManage(),
            ...(buttons && buttons.free),
        },
        legend: {
            theme: 'secondary',
            title: 'awarded on merit',
            loading,
            onClick: () => {},
            classes: [styles.legendBtn],
            ...(buttons && buttons.legend),
        },
        premium: {
            theme: 'secondary',
            title: 'Upgrade',
            loading,
            registration: false,
            onClick: () =>
                currentPlan?.type === 'premium'
                    ? handleManage()
                    : handleUpgrade(),
            classes: [styles.premium],
            ...(buttons && buttons.premium),
        },
        platinum: {
            theme: 'secondary',
            title: 'Upgrade',
            loading,
            registration: false,
            onClick: () =>
                currentPlan?.type === 'premium'
                    ? handleManage()
                    : handleUpgrade(),
            classes: [styles.platinum],
            ...(buttons && buttons.platinum),
        },
    };

    const color =
        data.type === 'contributing_editor'
            ? 'var(--color-mint-ink)'
            : data.type === 'platinum'
            ? 'var(--color-platinum)'
            : data.type === 'premium'
            ? 'var(--color-premium)'
            : 'var(--color-blue)';

    return (
        <>
            <div
                className={[
                    'border-gray',
                    styles.card,
                    currentPlan.type === data.type && styles.active,
                    styles[data.type],
                ].join(' ')}
            >
                {tag}
                {/*{data?.type === 'legend' ? tagLegend : undefined}*/}
                <div
                    className={
                        data?.type === 'legend'
                            ? styles.legendBackground
                            : undefined
                    }
                >
                    <p style={{ fontSize: 14 }}>
                        {currentPlan.type === data.type ? (
                            'Your active plan'
                        ) : (
                            <span>&nbsp;</span>
                        )}
                    </p>
                    <div className={styles.cardHeader}>
                        <h4>{name}</h4>
                        {data.sale && currentPlan.type !== data.type && (
                            <div
                                className={[
                                    styles.saleTag,
                                    styles[`${data?.type}Tag`],
                                ].join(' ')}
                            >
                                {data.sale.amount}
                            </div>
                        )}
                    </div>
                    <div className={styles.price}>
                        {(() => {
                            switch (data.type) {
                                case 'free':
                                    return 'Free';
                                case 'legend':
                                    return 'Earned';
                                default:
                                    return (
                                        <>
                                            {data.amountFormatted}
                                            <span>/{data.interval}</span>
                                            <span className={styles.vat}>
                                                {' '}
                                                + VAT
                                            </span>
                                        </>
                                    );
                            }
                        })()}
                    </div>
                </div>

                {nextBilling}

                {!upgrade && (
                    <p style={{ lineHeight: '145%' }}>
                        {subscriptions[data?.type]?.text}
                    </p>
                )}
                {!upgrade && (
                    <ul className={[CheckMarkList.list, styles.list].join(' ')}>
                        {(data.type === 'free'
                            ? subscriptions.free.list.map((name) => ({ name }))
                            : marketing_features
                        ).map((feature, index) => (
                            <li key={`${data?.type}-list-${index}`}>
                                <span>
                                    <CheckMark
                                        isLegend={data.type === 'legend'}
                                        fill={color}
                                        shadow={color}
                                    />
                                </span>
                                {feature.name}
                            </li>
                        ))}
                    </ul>
                )}

                {((data.type === 'legend' && currentPlan.type !== 'legend') ||
                    (data.type === 'free' &&
                        button[data.type].registration)) && (
                    <div className={styles.payment}>
                        <Button {...button[data.type]} />
                    </div>
                )}

                {/* Upgrade buttons */}
                {data.type === 'premium' && currentPlan.type === 'free' && (
                    <div className={styles.payment}>
                        {!upgrade && <Button {...button['premium']} />}
                    </div>
                )}
                {data.type === 'contributing_editor' &&
                    (currentPlan.type === 'free' ||
                        currentPlan.type === 'premium' ||
                        currentPlan.type === 'platinum') && (
                        <div className={styles.payment}>
                            {!upgrade && (
                                <Button {...button['contributing_editor']} />
                            )}
                        </div>
                    )}
                {data.type === 'platinum' &&
                    (currentPlan.type === 'free' ||
                        currentPlan.type === 'premium') && (
                        <div className={styles.payment}>
                            {!upgrade && <Button {...button['platinum']} />}
                        </div>
                    )}

                {/* Manage subscription */}
                {data.type === 'premium' &&
                    (currentPlan.type === 'premium' ||
                        button[data.type].registration) && (
                        <Button {...button['free']} />
                    )}
                {data.type === 'contributing_editor' &&
                    (currentPlan.type === 'contributing_editor' ||
                        button[data.type].registration) && (
                        <Button {...button['free']} />
                    )}
                {data.type === 'platinum' &&
                    (currentPlan.type === 'platinum' ||
                        button[data.type].registration) && (
                        <Button {...button['free']} />
                    )}
            </div>
        </>
    );
};

export default Card;
